import { useMemo, useState } from "react";
import { useCampaigns, useFlyerTypes } from "../api/campaigns";
import {
    useBulkDistributionDistributors,
    useBulkDistributionRuns,
    useBulkDistributionSources,
    usePreviewBulkDistribution,
    useRunBulkDistribution,
    useUndoBulkDistributionRun,
    type BulkDistributionPreview,
    type BulkDistributionSourceType
} from "../api/bulkDistribution";
import { showError, showSuccess, confirmAction } from "../shared/toast";
import "./bulk-distribution-panel.css";

const todayIso = (): string => new Date().toISOString().slice(0, 10);

const sourceTypeLabel = (sourceType: BulkDistributionSourceType): string =>
    sourceType === "SavedSet" ? "Uppsättning" : "Uppdrag";

const errorMessage = (error: unknown, fallback: string): string => {
    const response = (error as { response?: { data?: { error?: string; message?: string } } })?.response;
    return response?.data?.error ?? response?.data?.message ?? fallback;
};

export const BulkDistributionPanel = (): JSX.Element => {
    const [sourceKey, setSourceKey] = useState("");
    const [campaignId, setCampaignId] = useState("");
    const [flyerId, setFlyerId] = useState("");
    const [distributorUserId, setDistributorUserId] = useState("");
    const [deliveredOn, setDeliveredOn] = useState(todayIso());
    const [preview, setPreview] = useState<BulkDistributionPreview | null>(null);

    const { data: sources = [], isLoading: sourcesLoading } = useBulkDistributionSources();
    const { data: distributors = [] } = useBulkDistributionDistributors();
    const { data: campaigns = [] } = useCampaigns();
    const { data: flyers = [] } = useFlyerTypes(campaignId || null);
    const { data: runs = [], isLoading: runsLoading } = useBulkDistributionRuns();

    const previewMutation = usePreviewBulkDistribution();
    const runMutation = useRunBulkDistribution();
    const undoMutation = useUndoBulkDistributionRun();

    const savedSets = useMemo(() => sources.filter((s) => s.sourceType === "SavedSet"), [sources]);
    const assignments = useMemo(() => sources.filter((s) => s.sourceType === "Assignment"), [sources]);
    const selectedSource = useMemo(
        () => sources.find((s) => `${s.sourceType}:${s.id}` === sourceKey) ?? null,
        [sources, sourceKey]
    );

    // Any edit invalidates the numbers on screen — never let a stale preview justify a run.
    const resetPreview = () => setPreview(null);

    const canPreview = Boolean(selectedSource && campaignId && flyerId);
    const canRun = Boolean(preview && canPreview && distributorUserId);

    const handleSourceChange = (value: string) => {
        setSourceKey(value);
        resetPreview();

        // An assignment already knows its campaign, and the assignments table records which
        // flyer it hands out in the current round — preselect both to save two steps.
        const source = sources.find((s) => `${s.sourceType}:${s.id}` === value);
        if (source?.campaignId && source.campaignId !== campaignId) {
            setCampaignId(source.campaignId);
            setFlyerId(source.flyerId ?? "");
        } else if (source?.flyerId) {
            setFlyerId(source.flyerId);
        }
    };

    const handlePreview = async () => {
        if (!selectedSource) return;

        try {
            const result = await previewMutation.mutateAsync({
                sourceType: selectedSource.sourceType,
                sourceId: selectedSource.id,
                campaignId,
                flyerId
            });
            setPreview(result);
        } catch (error) {
            setPreview(null);
            showError("Kunde inte förhandsgranska", errorMessage(error, "Försök igen."));
        }
    };

    const handleRun = async () => {
        if (!selectedSource || !preview) return;

        const distributorName = distributors.find((d) => d.id === distributorUserId)?.name ?? "vald utdelare";
        const confirmed = await confirmAction(
            `Registrera ${preview.wouldCreateCount} utdelningar av "${preview.flyerName}" på ${preview.sourceName} som utdelade av ${distributorName} den ${deliveredOn}?`
        );
        if (!confirmed) return;

        try {
            const result = await runMutation.mutateAsync({
                sourceType: selectedSource.sourceType,
                sourceId: selectedSource.id,
                campaignId,
                flyerId,
                distributorUserId,
                deliveredOn
            });
            showSuccess(
                `${result.createdCount} utdelningar registrerade${result.skippedCount > 0 ? `, ${result.skippedCount} hoppades över (redan utdelade)` : ""}.`
            );
            setPreview(null);
        } catch (error) {
            showError("Kunde inte registrera utdelningarna", errorMessage(error, "Försök igen."));
        }
    };

    const handleUndo = async (runId: string, sourceName: string, addressCount: number) => {
        const confirmed = await confirmAction(
            `Ångra körningen på ${sourceName}? ${addressCount} utdelningar tas bort.`
        );
        if (!confirmed) return;

        try {
            const result = await undoMutation.mutateAsync(runId);
            showSuccess(`${result.deletedCount} utdelningar togs bort.`);
        } catch (error) {
            showError("Kunde inte ångra körningen", errorMessage(error, "Försök igen."));
        }
    };

    return (
        <section className="bulk-distribution-panel" aria-label="Massregistrera utdelning">
            <h2>Massregistrera utdelning</h2>
            <p className="bulk-distribution-panel__intro">
                Registrera en hel uppsättning postnummer eller ett helt uppdrag som utdelat av en utdelare —
                till exempel material som PostNord delat ut åt oss. Adresser som redan har flygbladet
                registrerat hoppas över, liksom dolda och blockerade adresser.
            </p>

            <div className="bulk-distribution-panel__form">
                <label className="bulk-distribution-panel__field">
                    <span>Uppsättning eller uppdrag</span>
                    <select
                        value={sourceKey}
                        onChange={(e) => handleSourceChange(e.target.value)}
                        disabled={sourcesLoading}
                    >
                        <option value="">{sourcesLoading ? "Laddar..." : "Välj källa"}</option>
                        {savedSets.length > 0 && (
                            <optgroup label="Uppsättningar">
                                {savedSets.map((source) => (
                                    <option key={source.id} value={`SavedSet:${source.id}`}>
                                        {source.name}
                                        {source.municipalityName ? ` (${source.municipalityName})` : ""}
                                    </option>
                                ))}
                            </optgroup>
                        )}
                        {assignments.length > 0 && (
                            <optgroup label="Uppdrag">
                                {assignments.map((source) => (
                                    <option key={source.id} value={`Assignment:${source.id}`}>
                                        {source.name}
                                        {source.municipalityName ? ` (${source.municipalityName})` : ""}
                                    </option>
                                ))}
                            </optgroup>
                        )}
                    </select>
                </label>

                <label className="bulk-distribution-panel__field">
                    <span>Kampanj</span>
                    <select
                        value={campaignId}
                        onChange={(e) => {
                            setCampaignId(e.target.value);
                            setFlyerId("");
                            resetPreview();
                        }}
                    >
                        <option value="">Välj kampanj</option>
                        {campaigns.map((campaign) => (
                            <option key={campaign.id} value={campaign.id}>
                                {campaign.name}
                            </option>
                        ))}
                    </select>
                </label>

                <label className="bulk-distribution-panel__field">
                    <span>Flygblad</span>
                    <select
                        value={flyerId}
                        onChange={(e) => {
                            setFlyerId(e.target.value);
                            resetPreview();
                        }}
                        disabled={!campaignId}
                    >
                        <option value="">{campaignId ? "Välj flygblad" : "Välj kampanj först"}</option>
                        {flyers.map((flyer) => (
                            <option key={flyer.id} value={flyer.id}>
                                {flyer.name}
                            </option>
                        ))}
                    </select>
                </label>

                <label className="bulk-distribution-panel__field">
                    <span>Utdelare</span>
                    <select value={distributorUserId} onChange={(e) => setDistributorUserId(e.target.value)}>
                        <option value="">Välj utdelare</option>
                        <optgroup label="Distributionsföretag">
                            {distributors
                                .filter((d) => d.isSystemAccount)
                                .map((distributor) => (
                                    <option key={distributor.id} value={distributor.id}>
                                        {distributor.name}
                                    </option>
                                ))}
                        </optgroup>
                        <optgroup label="Användare">
                            {distributors
                                .filter((d) => !d.isSystemAccount)
                                .map((distributor) => (
                                    <option key={distributor.id} value={distributor.id}>
                                        {distributor.name}
                                    </option>
                                ))}
                        </optgroup>
                    </select>
                </label>

                <label className="bulk-distribution-panel__field">
                    <span>Utdelningsdatum</span>
                    <input
                        type="date"
                        value={deliveredOn}
                        max={todayIso()}
                        onChange={(e) => setDeliveredOn(e.target.value)}
                    />
                </label>

                <div className="bulk-distribution-panel__actions">
                    <button
                        type="button"
                        className="button"
                        onClick={handlePreview}
                        disabled={!canPreview || previewMutation.isPending}
                    >
                        {previewMutation.isPending ? "Räknar..." : "Förhandsgranska"}
                    </button>
                    <button
                        type="button"
                        className="button button--primary"
                        onClick={handleRun}
                        disabled={!canRun || runMutation.isPending}
                        title={preview ? undefined : "Förhandsgranska först"}
                    >
                        {runMutation.isPending ? "Registrerar..." : "Registrera utdelning"}
                    </button>
                </div>
            </div>

            {preview && (
                <div className="bulk-distribution-panel__preview">
                    <h3>
                        {preview.sourceName} — {preview.campaignName} / {preview.flyerName}
                    </h3>
                    <ul className="bulk-distribution-panel__summary">
                        <li>
                            <strong>{preview.addressCount}</strong> adresser i källan
                        </li>
                        <li>
                            <strong>{preview.wouldCreateCount}</strong> registreras
                        </li>
                        <li>
                            <strong>{preview.alreadyDeliveredCount}</strong> redan utdelade
                        </li>
                        <li>
                            <strong>{preview.skippedHiddenOrBlockedCount}</strong> dolda/blockerade
                        </li>
                    </ul>
                    <table className="bulk-distribution-table">
                        <thead>
                            <tr>
                                <th>Postnummer</th>
                                <th>Adresser</th>
                                <th>Redan utdelade</th>
                            </tr>
                        </thead>
                        <tbody>
                            {preview.items.map((item) => (
                                <tr key={item.label}>
                                    <td>{item.label}</td>
                                    <td>{item.addressCount}</td>
                                    <td>{item.alreadyDeliveredCount}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            <h3 className="bulk-distribution-panel__runs-title">Tidigare körningar</h3>
            {runsLoading ? (
                <p>Laddar...</p>
            ) : runs.length === 0 ? (
                <p>Inga massregistreringar gjorda ännu.</p>
            ) : (
                <table className="bulk-distribution-table">
                    <thead>
                        <tr>
                            <th>Källa</th>
                            <th>Typ</th>
                            <th>Kampanj</th>
                            <th>Flygblad</th>
                            <th>Utdelare</th>
                            <th>Utdelningsdatum</th>
                            <th>Adresser</th>
                            <th />
                        </tr>
                    </thead>
                    <tbody>
                        {runs.map((run) => (
                            <tr key={run.runId}>
                                <td>{run.sourceName}</td>
                                <td>{sourceTypeLabel(run.sourceType)}</td>
                                <td>{run.campaignName}</td>
                                <td>{run.flyerName}</td>
                                <td>{run.distributorName}</td>
                                <td>{new Date(run.deliveredAt).toLocaleDateString("sv-SE")}</td>
                                <td>{run.addressCount}</td>
                                <td>
                                    <button
                                        type="button"
                                        className="button button--danger"
                                        onClick={() => handleUndo(run.runId, run.sourceName, run.addressCount)}
                                        disabled={undoMutation.isPending}
                                    >
                                        Ångra
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            )}
        </section>
    );
};
